// ─────────────────────────────────────────────────────────────────────────────
// Axios API client with token refresh interceptor
// ─────────────────────────────────────────────────────────────────────────────
import axios from 'axios'

const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

export const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
  timeout: 120_000,
})

// Request interceptor — add auth token from store
api.interceptors.request.use((config) => {
  // Token is already set in defaults on login — but read from storage for safety
  const stored = localStorage.getItem('estatekey-auth')
  if (stored) {
    const { state } = JSON.parse(stored)
    if (state?.accessToken && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${state.accessToken}`
    }
  }
  return config
})

// Response interceptor — handle 401 with token refresh
let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        }).then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`
          return api(originalRequest)
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const stored = localStorage.getItem('estatekey-auth')
        if (!stored) throw new Error('No auth state')
        const { state } = JSON.parse(stored)
        const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refreshToken: state.refreshToken })

        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
        processQueue(null, data.accessToken)

        originalRequest.headers['Authorization'] = `Bearer ${data.accessToken}`
        return api(originalRequest)
      } catch (err) {
        processQueue(err, null)
        // Redirect to login
        window.location.href = '/login'
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

// Typed query helpers
export const apiGet = <T>(url: string, params?: object) =>
  api.get<T>(url, { params }).then((r) => r.data)

export const apiPost = <T>(url: string, data?: object) =>
  api.post<T>(url, data).then((r) => r.data)

export const apiPatch = <T>(url: string, data?: object) =>
  api.patch<T>(url, data).then((r) => r.data)

export const apiPut = <T>(url: string, data?: object) =>
  api.put<T>(url, data).then((r) => r.data)

export const apiDelete = <T>(url: string) =>
  api.delete<T>(url).then((r) => r.data)
